import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Printer, Share2 } from "lucide-react";
import useAgentShared from "../../hooks/agent/useAgentShared";
import AivySideBanner from "../../components/common/AivySideBanner";
import DaiumCareBanner from "../../components/common/DaiumCareBanner";
import ShareModal from "../../components/common/ShareModal";
import Header from "../../components/ui/Header";
import Footer from "../../components/ui/Footer";
import { toDataUri } from "../../utils/fileUtils";

const AgentSharedResult = () => {
  const { resultSid } = useParams<{ resultSid: string }>();
  const navigate = useNavigate();
  const { getSharedResult, loading, error, clearError } = useAgentShared();
  const [sharedData, setSharedData] = useState<any>(null);
  const [images, setImages] = useState<string[]>([]);
  const [showShareModal, setShowShareModal] = useState(false);

  const loadSharedResult = useCallback(async () => {
    if (!resultSid) return;

    clearError();
    const result = await getSharedResult(resultSid);
    if (result) {
      setSharedData(result);

      if (result.binary_values) {
        try {
          // binary_values가 JSON 문자열인 경우 파싱
          const binaryData = JSON.parse(result.binary_values);
          if (Array.isArray(binaryData)) {
            setImages(
              binaryData.filter((url) => url && typeof url === "string")
            );
          } else if (typeof binaryData === "string") {
            setImages([binaryData]);
          }
        } catch {
          // JSON 파싱 실패 시 단일 URL로 처리
          if (
            typeof result.binary_values === "string" &&
            result.binary_values.trim()
          ) {
            setImages([result.binary_values]);
          }
        }
      }
    }
  }, [resultSid, clearError, getSharedResult]);

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");

    const imagesHtml = images
      .map((img, idx) => {
        const imgSrc =
          typeof img === "string"
            ? img
            : typeof img === "object"
            ? toDataUri(img as { data: string; ext: string })
            : toDataUri(img);

        return `
        <div style="margin-bottom: 20px; text-align: center;">
          <img src="${imgSrc}" style="max-width: 100%; height: 100%; display: block; margin: 0 auto;" />
        </div>
      `;
      })
      .join("");

    printWindow?.document.write(`
      <html>
        <head>
          <title>이미지 인쇄</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              padding: 15px;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
              display: flex;
              flex-direction: column;
              align-items: center;
            }
            @media print {
              body { 
                padding: 10px;
              }
              @page {
                size: A4;
                margin: 10mm;
              }
              /* 브라우저 기본 헤더/푸터 제거 */
              @page {
                margin-top: 0;
                margin-bottom: 0;
              }
              body {
                margin: 0;
              }
              img {
                max-width: 100%;
                height: 100%;
              }
            }
          </style>
        </head>
        <body>
          ${imagesHtml}
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                window.close();
              }, 250);
            };
          </script>
        </body>
      </html>
    `);

    printWindow?.document.close();
  };

  useEffect(() => {
    if (resultSid) {
      loadSharedResult();
    }
  }, [loadSharedResult, resultSid]);

  const handleShare = () => {
    if (!resultSid) return;
    setShowShareModal(true);
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-50">
          <AivySideBanner />
          <DaiumCareBanner />
          <div className="max-w-7xl mx-auto px-5 py-8">
            <div className="flex justify-center items-center min-h-[700px]">
              <div className="text-center">
                <div className="flex justify-center mb-8">
                  <div className="relative">
                    <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-800 rounded-full animate-spin"></div>
                  </div>
                </div>
                <h2 className="text-xl font-semibold text-gray-800 mb-2">
                  공유된 결과를 불러오는 중...
                </h2>
                <p className="text-sm text-gray-500">잠시만 기다려주세요</p>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-50">
          <AivySideBanner />
          <DaiumCareBanner />
          <div className="max-w-7xl mx-auto px-5 py-8">
            <div className="flex justify-center items-center min-h-[700px]">
              <div className="text-center">
                <div className="flex justify-center mb-8">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                    <span className="text-red-600 text-2xl">⚠</span>
                  </div>
                </div>
                <h2 className="text-xl font-semibold text-red-900 mb-2">
                  결과를 불러올 수 없습니다
                </h2>
                <p className="text-sm text-red-700 mb-6">{error}</p>
                <button
                  onClick={() => navigate("/")}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  홈으로 돌아가기
                </button>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  if (!sharedData) {
    return null;
  }

  return (
    <>
      <Header />
      <AivySideBanner />
      <DaiumCareBanner />
      <div className="max-w-6xl mx-auto px-5 py-12">
        <div className="min-h-screen bg-gray-50">
          <div className="mb-8 font-serif-kr">
            <button
              onClick={() => navigate("/")}
              className="text-sm md:text-base inline-flex items-center gap-2 px-4 py-2 font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 shadow-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              홈으로 돌아가기
            </button>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm font-serif-kr">
            <div className="px-4 md:px-8 py-4 md:py-6 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-white text-[20px]">📄</span>
                  <h1 className="text-base md:text-xl font-semibold text-slate-900 tracking-tight">
                    공유된 AI 분석 결과
                  </h1>
                </div>
                <button
                  onClick={handleShare}
                  className="text-sm md:text-base inline-flex items-center gap-2 px-2 md:px-4 py-2 font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 shadow-sm"
                >
                  <Share2 className="w-4 h-4" />
                  공유하기
                </button>
              </div>
            </div>

            <div className="px-6 md:px-8 py-6">
              <div className="text-base md:text-lg prose prose-slate max-w-none text-slate-700 leading-snug prose-headings:text-slate-900 prose-p:text-slate-600 prose-strong:text-slate-800 prose-code:text-slate-800 prose-code:bg-slate-100 prose-pre:bg-slate-50 whitespace-pre-line">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {sharedData.output_values}
                </ReactMarkdown>
              </div>

              {images.length > 0 && (
                <>
                  <div className="border-t border-slate-100 mt-8 pt-8">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <span className="text-white text-[20px]">🖼</span>
                        <h3 className="text-lg font-semibold text-slate-900 tracking-tight">
                          생성된 이미지
                        </h3>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="bg-slate-100 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600">
                          {images.length}개
                        </div>
                        <button
                          onClick={handlePrint}
                          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 shadow-sm"
                        >
                          <Printer className="w-4 h-4" />
                          인쇄
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {images.map((img, idx) => (
                        <div
                          key={idx}
                          className="group bg-slate-50 rounded-xl p-4 border border-slate-200 hover:border-slate-300 transition-all duration-200"
                        >
                          <img
                            src={img}
                            alt={`shared-result-${idx}`}
                            className="w-full h-auto rounded-lg shadow-sm"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = "none";
                              const parent = target.parentElement;
                              if (parent) {
                                const fallbackDiv =
                                  document.createElement("div");
                                fallbackDiv.className =
                                  "w-full aspect-square bg-slate-100 rounded-lg flex items-center justify-center";
                                fallbackDiv.innerHTML = `
                                  <div class="text-center">
                                    <div class="text-slate-400 text-sm mb-2">🖼</div>
                                    <div class="text-xs text-slate-500">이미지를 불러올 수 없습니다</div>
                                  </div>
                                `;
                                parent.appendChild(fallbackDiv);
                              }
                            }}
                          />
                          <div className="mt-3 text-center">
                            <span className="text-xs font-medium text-slate-500 tracking-wide">
                              이미지 {idx + 1}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        shareUrl={`${window.location.origin}/agent/share/${resultSid}`}
        title="공유된 AI 분석 결과"
      />
      <Footer />
    </>
  );
};

export default AgentSharedResult;
